import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  message,
  Radio,
  Row,
  Select,
  Upload,
} from "antd";
import { useWatch } from "antd/es/form/Form";
import { Trash, Plus, Inbox, ExternalLink } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ConfirmAction from "../../Components/ConfirmAction/ConfirmAction";
import ReasonModal from "../../Components/CustomModals/ReasonModal";
import ViewAttachmentModal from "../../Components/CustomModals/ViewAttachmentModal";
import {
  formatNumbers,
  formatString,
  navigateTo,
} from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useParams } from "react-router";

const { Dragger } = Upload;

interface PurchaseOrderFormProps {
  add?: boolean;
  edit?: boolean;
  view?: boolean;
  approval?: boolean;
}

const PurchaseOrderForm: React.FC<PurchaseOrderFormProps> = ({ add, edit, view, approval }) => {
  const [loading,               setLoading]               = useState("");
  const [showDisapproveModal,   setShowDisapproveModal]   = useState(false);
  const [form]                                            = Form.useForm();
  const [expenseTypes,          setExpenseTypes]          = useState<any[]>([]);
  const [branches,              setBranches]              = useState<any[]>([]);
  const [purchaseRequests,      setPurchaseRequests]      = useState<any[]>([]);
  const [prSelected,            setPrSelected]            = useState(false);
  const [withholdingTaxPercent, setWithholdingTaxPercent] = useState(0);
  const [disapprovalReason,     setDisapprovalReason]     = useState("");
  const [attachmentUrls,        setAttachmentUrls]        = useState<string[]>([]);
  const [showAttachment,        setShowAttachment]        = useState<string | null>(null);

  const itemsArray = useWatch("itemsArray", form);
  const { id: purchase_request_id } = useParams<{ id: string }>();
  const isReadOnly = view || add || approval;

  function clearFields() {
    form.resetFields();
  }

  const subTotal = (itemsArray || []).reduce((sum: number, item: any) => {
    const qty   = Number(item?.qty)   || 0;
    const price = Number(item?.price) || 0;
    return sum + qty * price;
  }, 0);

  const withholdingTaxAmount = subTotal * (withholdingTaxPercent / 100);
  const grandTotal           = subTotal - withholdingTaxAmount;

  const handleBeforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) message.error(`${file.name} exceeds the 2MB size limit.`);
    return false;
  };

  const handleUploadChange = (info: any) => {
    const validFiles = info.fileList.filter((f: any) => f.size / 1024 / 1024 < 2);
    form.setFieldValue("attachment", validFiles);
  };

  async function submitForm(data: any) {
    const formattedItemsArray = (data.itemsArray || []).map((item: any) => ({
      ...item,
      total_amount: parseFloat(
        ((Number(item.qty) || 0) * (Number(item.price) || 0)).toFixed(2)
      ),
    }));

    const formData = new FormData();
    formData.append("expense_date",        data.transaction_date ? dayjs(data.transaction_date).format("YYYY-MM-DD") : "");
    formData.append("itemsArray",          JSON.stringify(formattedItemsArray));
    formData.append("grand_total",         String(grandTotal));
    formData.append("wht_percent",         String(withholdingTaxPercent));
    formData.append("wht_amount",          String(withholdingTaxAmount));
    formData.append("purchase_order_id",   String(purchase_request_id ?? ""));
    formData.append("supplier_name",       String(data.supplier_name ?? ""));
    formData.append("branch_id",           String(data.branch_id ?? ""));
    formData.append("remarks",             String(data.remarks ?? ""));
    formData.append("purchase_request_id", String(data.purchase_request_id ?? ""));

    const attachments = data.attachment || [];
    attachments.forEach((f: any) => {
      if (f.originFileObj) formData.append("attachment[]", f.originFileObj);
    });

    setLoading("submit");
    const response = await postRequest(
      add ? `purchase_orders/create` : `purchase_orders/update`,
      formData,
      true
    );

    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { clearFields(); setLoading(""); navigateTo("/purchase-order"); }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${add ? "create" : "update"} purchase order.`
      );
    }
  }

  async function submitApprove() {
    setLoading("approve");
    const response = await postRequest(
      `purchase_orders/change_status`,
      { purchase_order_id: purchase_request_id, action: "approved" },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { clearFields(); setLoading(""); navigateTo("/purchase-order"); }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to approve purchase order."
      );
    }
  }

  async function submitDisapprove() {
    if (!disapprovalReason.trim()) {
      message.error("Please provide a reason for disapproval.");
      return;
    }
    setShowDisapproveModal(false);
    setLoading("disapprove");
    const response = await postRequest(
      `purchase_orders/change_status`,
      { purchase_order_id: purchase_request_id, action: "disapproved", reason: disapprovalReason },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { clearFields(); setLoading(""); navigateTo("/purchase-order"); }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to disapprove purchase order."
      );
    }
  }

  async function fetchBranches() {
    setBranches([]);
    const response = await getRequest(`branches/get`, {}, true);
    if (response.success) setBranches(response.data.data);
  }

  async function fetchExpenseTypes() {
    setExpenseTypes([]);
    const response = await getRequest(`expense_types/get`, {}, true);
    if (response.success) setExpenseTypes(response.data.data);
  }

  async function fetchPurchaseRequests() {
    setPurchaseRequests([]);
    const response = await getRequest(`purchase_requests/get`, { status: "approved", payment_method: "credit" }, true);
    if (response.success) setPurchaseRequests(response.data.data);
  }

  const fetchPurchaseRequestData = (value?: any) => {
    const id = value ?? purchase_request_id;

    if (!id) {
      form.resetFields();
      setPrSelected(false);
      setWithholdingTaxPercent(1);
      return;
    }

    setLoading("fetch");

    if (add) {
      getRequest(`purchase_requests/get`, { purchase_request_id: id }, true).then((response) => {
        if (response.success) {
          const data  = response.data.data[0];
          const items = data.items.map((row: any) => ({
            item_id:         row.item_id,
            item_name:       row.item_name,
            expense_type_id: Number(row.expense_type_id),
            qty:             Number(row.qty),
            unit:            row.unit,
            price:           Number(row.price),
          }));
          form.setFieldsValue({
            supplier_id:      Number(data.supplier_id),
            supplier_name:    data.supplier_name,
            branch_id:        Number(data.branch_id),
            transaction_date: data.expense_date ? dayjs(data.expense_date) : null,
            remarks:          data.remarks,
            itemsArray:       items,
          });
          setWithholdingTaxPercent(Number(data.wht_percentage) || 1);

          // ── Support both array and legacy single attachment ──
          if (Array.isArray(data.attachments)) {
            setAttachmentUrls(data.attachments);
          } else if (data.attachment_url) {
            setAttachmentUrls([data.attachment_url]);
          } else {
            setAttachmentUrls([]);
          }

          setPrSelected(true);
        }
        setLoading("");
      });
    } else {
      getRequest(`purchase_orders/get`, { purchase_order_id: id }, true).then((response) => {
        if (response.success) {
          const data  = response.data.data[0];
          const items = data.items.map((row: any) => ({
            item_id:         row.item_id,
            item_name:       row.item_name,
            expense_type_id: Number(row.expense_type_id),
            qty:             Number(row.qty),
            unit:            row.unit,
            price:           Number(row.price),
          }));
          form.setFieldsValue({
            purchase_request_id: Number(data.purchase_request_id),
            supplier_id:         Number(data.supplier_id),
            supplier_name:       data.supplier_name,
            branch_id:           Number(data.branch_id),
            transaction_date:    data.expense_date ? dayjs(data.expense_date) : null,
            remarks:             data.remarks,
            itemsArray:          items,
          });
          setWithholdingTaxPercent(Number(data.wht_percentage) || 1);

          // ── Support both array and legacy single URL ──
          if (Array.isArray(data.attachments)) {
            setAttachmentUrls(data.attachments);
          } else if (data.attachment_url) {
            setAttachmentUrls([data.attachment_url]);
          } else {
            setAttachmentUrls([]);
          }

          setPrSelected(true);
        }
        setLoading("");
      });
    }
  };

  const handlePRChange = (value: any) => {
    fetchPurchaseRequestData(value);
  };

  useEffect(() => {
    if (edit || view || approval) {
      fetchPurchaseRequestData();
    }
  }, []);

  useEffect(() => {
    fetchExpenseTypes();
    fetchBranches();
    fetchPurchaseRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title={add ? "Add Purchase Order" : edit ? "Edit Purchase Order" : "View Purchase Order"}
              withDateRangeFilter={false}
              withAddButton={false}
              withSearchBtn={false}
            />
          </Col>
          <div className="form-container">
            <Col span={24}>
              <Form form={form} onFinish={submitForm} layout="vertical">
                <Row className="w-full" gutter={[10, 10]}>

                  {add && (
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="PR No."
                        name="purchase_request_id"
                        rules={[{ required: true, message: "This field is required." }]}
                      >
                        <Select
                          placeholder="Select purchase request"
                          showSearch
                          allowClear
                          optionFilterProp="label"
                          onChange={handlePRChange}
                          options={purchaseRequests.map((info: any) => ({
                            label: `PR No. - ${info.id}`,
                            value: Number(info.id),
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  )}

                  <Col xs={24} lg={12}>
                    <Form.Item
                      label="Supplier Name"
                      name="supplier_name"
                      rules={[{ required: true, whitespace: true, message: "This field is required." }]}
                    >
                      <Input disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={12}>
                    <Form.Item
                      label="Branch"
                      name="branch_id"
                      rules={[{ required: true, message: "This field is required." }]}
                    >
                      <Select
                        placeholder="Select branch"
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        disabled={isReadOnly}
                        options={branches.map((branch: any) => ({
                          label: branch.name,
                          value: Number(branch.id),
                        }))}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={12}>
                    <Form.Item
                      label="Transaction Date"
                      name="transaction_date"
                      rules={[{ required: true, message: "This field is required." }]}
                    >
                      <DatePicker className="w-full" format="MMMM D, YYYY" disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item label="Remarks" name="remarks">
                      <Input.TextArea disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <span className="edit-label mb-2">
                      <label className="text-red-500 me-1"> *</label>List of Items:
                    </span>
                    <Form.List name="itemsArray" initialValue={[{}]}>
                      {(fields, { add, remove }) => (
                        <>
                          <Col xs={24}>
                            <div className="overflow-auto max-h-[600px]">
                              <table className="w-full table-fixed border border-collapse min-w-[800px]">
                                <thead>
                                  <tr className="bg-primary text-white font-bold border">
                                    <th className="p-2 border w-[200px]">Item Name</th>
                                    <th className="p-2 border w-[150px]">Expense Type</th>
                                    <th className="p-2 border w-[80px]">Qty</th>
                                    <th className="p-2 border w-[100px]">Price</th>
                                    <th className="p-2 border w-[100px]">Total</th>
                                    {!isReadOnly && (
                                      <th className="p-2 border w-[60px] text-center">Action</th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {fields.map((field) => (
                                    <tr key={field.key} className="border bg-white">
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "item_name"]} rules={[{ required: true, message: "Required" }]} noStyle>
                                          <Input disabled={isReadOnly} />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "expense_type_id"]} rules={[{ required: true, message: "Required" }]} noStyle>
                                          <Select
                                            className="w-full"
                                            placeholder="Select expense type"
                                            showSearch
                                            allowClear
                                            optionFilterProp="label"
                                            disabled={isReadOnly}
                                            options={expenseTypes.map((type: any) => ({
                                              label: type.name,
                                              value: Number(type.id),
                                            }))}
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "qty"]} rules={[{ required: true, message: "Required" }]} noStyle>
                                          <Input
                                            type="number"
                                            min={1}
                                            disabled={isReadOnly}
                                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "price"]} rules={[{ required: true, message: "Required" }]} noStyle>
                                          <Input
                                            type="number"
                                            min={0}
                                            step="0.01"
                                            disabled={isReadOnly}
                                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item shouldUpdate noStyle>
                                          {() => {
                                            const qty   = form.getFieldValue(["itemsArray", field.name, "qty"]);
                                            const price = form.getFieldValue(["itemsArray", field.name, "price"]);
                                            const total = (Number(qty) || 0) * (Number(price) || 0);
                                            return <Input value={formatNumbers(total)} disabled />;
                                          }}
                                        </Form.Item>
                                      </td>
                                      {!isReadOnly && (
                                        <td className="p-2 border text-center">
                                          {fields.length !== 1 && (
                                            <Button onClick={() => remove(field.name)} icon={<Trash size={17} />} danger type="text" />
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Col>
                          <Col xs={24}>
                            <Row>
                              <Col span={17} className="mt-2">
                                {!prSelected && !isReadOnly && (
                                  <Button type="primary" onClick={() => add()} icon={<Plus size={16} />}>
                                    Add Item
                                  </Button>
                                )}
                              </Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Subtotal:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {formatNumbers(subTotal)}</Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Withholding Tax:</strong></Col>
                              <Col span={5} className="text-start ps-5">
                                <Radio.Group
                                  value={withholdingTaxPercent}
                                  onChange={(e) => setWithholdingTaxPercent(e.target.value)}
                                  disabled={isReadOnly}
                                >
                                  <Radio value={0}>0%</Radio>
                                  <Radio value={1}>1%</Radio>
                                  <Radio value={2}>2%</Radio>
                                </Radio.Group>
                              </Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Calculated Withholding Tax:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {formatNumbers(withholdingTaxAmount)}</Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Grand Total:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {formatNumbers(grandTotal)}</Col>
                            </Row>
                          </Col>
                        </>
                      )}
                    </Form.List>
                  </Col>

                  {/* ── View attachments (read-only) ── */}
                  {(view || approval) && attachmentUrls.length > 0 && (
                    <Col xs={24} className="pt-1 mt-4">
                      <span className="font-bold block mb-2">Attachment(s)</span>
                      <div className="flex flex-wrap gap-2">
                        {attachmentUrls.map((url, index) => (
                          <Button
                            key={index}
                            icon={<ExternalLink size={14} />}
                            onClick={() => setShowAttachment(url)}
                          >
                            Attachment {index + 1}
                          </Button>
                        ))}
                      </div>
                    </Col>
                  )}

                  {/* ── Upload (add / edit) ── */}
                  {(add || edit) && (
                    <Col xs={24} className="mt-5 pt-5">
                      <Form.Item
                        label="Attachment(s)"
                        name="attachment"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}
                      >
                        <Dragger
                          name="file"
                          multiple
                          beforeUpload={handleBeforeUpload}
                          onChange={handleUploadChange}
                          accept=".pdf,image/*"
                        >
                          <p className="ant-upload-drag-icon flex justify-center items-center text-3xl"><Inbox /></p>
                          <p className="ant-upload-text">Click or drag files to upload</p>
                          <p className="ant-upload-hint">Supports PDF, Image formats. You can upload multiple files.</p>
                        </Dragger>
                        <p className="text-textColor pt-2">Note: Maximum file size is 2MB per file.</p>
                      </Form.Item>
                    </Col>
                  )}

                  <Col lg={24} className="mt-5 flex justify-end gap-3">
                    <Button onClick={() => { navigateTo("/purchase-order"); clearFields(); }}>
                      {view ? "Close" : "Cancel"}
                    </Button>

                    {!view && !approval && (
                      <Button htmlType="submit" type="primary" loading={loading === "submit"}>
                        {add ? "Submit" : "Update"}
                      </Button>
                    )}

                    {approval && (
                      <>
                        <Button
                          danger
                          loading={loading === "disapprove"}
                          onClick={() => {
                            setDisapprovalReason("");
                            setShowDisapproveModal(true);
                          }}
                        >
                          Disapprove
                        </Button>
                        <ConfirmAction
                          title="Are you sure you want to approve this purchase order?"
                          onConfirm={submitApprove}
                          okText="Approve"
                          cancelText="Cancel"
                          okButtonProps={{ loading: loading === "approve" }}
                        >
                          <Button type="primary" loading={loading === "approve"}>
                            Approve
                          </Button>
                        </ConfirmAction>
                      </>
                    )}
                  </Col>

                </Row>
              </Form>
            </Col>
          </div>
        </Row>
      </Layout>
      <PersistentFooter />

      <ViewAttachmentModal
        showModal={!!showAttachment}
        attachmentUrl={showAttachment ?? ""}
        onCancel={() => setShowAttachment(null)}
      />

      <ReasonModal
        showModal={showDisapproveModal}
        onCancel={() => setShowDisapproveModal(false)}
        title="Please provide a reason for disapproval:"
        reasonValue={disapprovalReason}
        onReasonChange={(e: any) => setDisapprovalReason(e.target.value)}
        footer={[
          <Button key="cancel" onClick={() => setShowDisapproveModal(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={loading === "disapprove"}
            disabled={!disapprovalReason.trim()}
            onClick={submitDisapprove}
          >
            Confirm Disapprove
          </Button>,
        ]}
      />
    </Layout>
  );
};

export default PurchaseOrderForm;